import { Config } from 'config/envConfig';
import React, { useEffect, useState } from 'react';
import { alert, AlertType, Button, ConfirmModal } from '@quyva/shared-ui';
import {
    faCircleXmark,
    faExclamationTriangle,
    faFileImport,
} from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { getUrl } from '../../../utils';
import axios from 'axios';
import JSZip from 'jszip';
import { checkIfFileExists, validateFile } from './utils';
import { getFileNameWithoutExt } from '@quyva/common';

export const ImportLayoutView = ({
    selectedFiles,
    setSelectedFiles,
    error,
    setError,
    setViewImportModal,
    user,
    setLoadingText,
    setIsLoading,
    fetchTemplateList,
    setTemplateList,
    templateList,
    handleLoadTemplate,
    getLayoutsList,
    setSelectedTemplate,
    setSelectedLayout,
    setAddNewTemplateMode,
}) => {
    const [viewConfirmModal, setViewConfirmModal] = useState(false);
    const [templateExist, setTemplateExist] = useState(false);
    const [templateName, setTemplateName] = useState('');

    useEffect(() => {
        if (selectedFiles && selectedFiles.length > 0) {
            validateZipFile(selectedFiles[0]);
        }
    }, [selectedFiles]);

    const processZipFile = async (file) => {
        try {
            const zip = new JSZip();
            const zipContent = await zip.loadAsync(file);
            let foundValidFile = false;

            for (const fileName of Object.keys(zipContent.files)) {
                const extension = fileName.split('.').pop().toLowerCase();

                if (extension === 'csv' || extension === 'py') {
                    setTemplateName(fileName);

                    foundValidFile = true;
                    const fileContent = await zipContent.files[fileName].async(
                        'text'
                    );

                    const {
                        isNameWithExtensionSame,
                        isNameWithoutExtensionSame,
                    } = checkIfFileExists(fileName, templateList);

                    if (
                        isNameWithoutExtensionSame &&
                        !isNameWithExtensionSame
                    ) {
                        // display error that a template with same name but different extension already exists.
                        setError(
                            'Template with same name but different extension already exists.'
                        );
                        return;
                    } else if (
                        isNameWithExtensionSame &&
                        isNameWithoutExtensionSame
                    ) {
                        setTemplateExist(true);
                    }

                    const validationResponse = await validateFile(
                        fileName,
                        fileContent,
                        setError
                    );

                    if (!validationResponse.isValid) {
                        setError(`File validation failed for :  ${fileName}`);
                        return;
                    }
                }
                if (fileName.toLowerCase().includes('default')) {
                    foundValidFile = true;
                }
            }

            if (!foundValidFile) {
                setError(
                    'ZIP file must contain at least one .csv or .py file.'
                );
            }
        } catch (error) {
            console.error('Error processing ZIP file:', error);
            setError('Failed to read ZIP file.');
        }
    };

    // Validate file types
    const validateZipFile = (file) => {
        setError('');

        if (!file) {
            setError('Please select a file to upload.');
            return;
        }

        // Ensure only ZIP files are uploaded
        if (
            file.type !== 'application/zip' &&
            file.type !== 'application/x-zip-compressed'
        ) {
            setError('Please upload a valid ZIP file.');
            return;
        }

        processZipFile(file);
    };

    const importLayout = async (file, updateExistingTemplate = true) => {
        try {
            setLoadingText('Importing Layouts...');
            setIsLoading(false);

            const { email } = user;

            const formData = new FormData();
            formData.append('zipFile', file);
            formData.append('userId', email);
            formData.append('updateExistingTemplate', updateExistingTemplate);

            const dynamicUrl = getUrl(
                `http://${Config.NODE_SERVER_HOST}:${Config.NODE_SERVER_PORT}/layout/import`
            );

            const authToken = encodeURIComponent(user.token);

            const response = await axios.post(dynamicUrl, formData, {
                headers: {
                    'Content-Type': 'multipart/form-data',
                    Authorization: authToken,
                },
            });

            if (response.status === 200) {
                const { message, template } = response.data;
                alert(AlertType.SUCCESS, `Layout imported successfully`);
                await fetchTemplateList(user, () => {}, setTemplateList);

                const templateData = templateList.find(
                    (data) => (data.filename = template)
                );
                if (templateData) {
                    await handleLoadTemplate(user.email, template);
                    await getLayoutsList(template);

                    setSelectedTemplate(templateData);
                    setSelectedLayout(null);
                    setAddNewTemplateMode(true);
                }

                setSelectedFiles([]);
                setViewImportModal(false);
            } else {
                alert(AlertType.ERROR, 'Layout Import failed');
            }
        } catch (error) {
            alert(
                AlertType.ERROR,
                error.response?.data?.error || error.message
            );
        } finally {
            setLoadingText('');
            setIsLoading(false);
        }
    };

    // Handle form submission
    const handleImport = async () => {
        if (templateExist) {
            setViewConfirmModal(true);
        } else {
            await importLayout(selectedFiles[0], true);
            setViewImportModal(false);
        }
    };

    return (
        <div className='flex flex-col justify-center'>
            <div className='min-h-[20px] flex justify-center items-center text-buttons-alert400 mt-3'>
                {error && (
                    <FontAwesomeIcon
                        icon={faExclamationTriangle}
                        className='mr-2'
                    />
                )}
                {error}
            </div>

            <div className='flex items-center justify-center mt-4'>
                <div className='flex items-center justify-between w-full'>
                    <Button
                        type='danger'
                        icon={faCircleXmark}
                        className='w-1/3'
                        text={'Cancel'}
                        hoverText={'Cancel and close the modal'}
                        onClick={() => {
                            setViewImportModal(false);
                        }}
                    />
                    <Button
                        type='success'
                        icon={faFileImport}
                        className='w-1/3'
                        text={'Import'}
                        hoverText={'Import Layouts'}
                        onClick={async () => {
                            await handleImport();
                        }}
                        disabled={error || !selectedFiles[0]}
                    />
                </div>
            </div>

            {viewConfirmModal && (
                <ConfirmModal
                    modalText={`A template with name '${getFileNameWithoutExt(
                        templateName
                    )}' already exists. Do you want to update the existing template with your importing one?`}
                    confirmText={'Yes'}
                    cancelText={'No'}
                    onConfirm={async () => {
                        await importLayout(selectedFiles[0], true);
                        setViewConfirmModal(false);
                        setViewImportModal(false);
                    }}
                    onCancel={async () => {
                        await importLayout(selectedFiles[0], false);
                        setViewConfirmModal(false);
                        setViewImportModal(false);
                    }}
                />
            )}
        </div>
    );
};
